import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasterData } from "@/hooks/use-master-data";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/ui/multi-select";
import { EXPORT_ALL_FIELDS, AssignDialog, ALL_COLUMNS, COL_MAP, DEFAULT_VISIBLE_COLS, COL_STORAGE_KEY, renderLeadCell, contactedThisCycle } from "@/pages/Leads";
import ColumnChooserDialog from "@/components/crm/ColumnChooserDialog";
import { maskPhone } from "@/lib/phone";
import { LEAD_COLOR_BG } from "@/components/crm/LeadBulkDialogs";
import LeadActionsMenu from "@/components/crm/LeadActionsMenu";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, Search, RotateCcw, ChevronLeft, ChevronRight, Plus, X, CalendarIcon, SlidersHorizontal } from "lucide-react";
import { format, parseISO } from "date-fns";

const RESULT_LIMIT = 50;

// ── Field catalog for the rule builder ──────────────────────────────────────
// `type` governs which operators + which value input a field gets. `optKey`
// points a field at a master-data option list (rendered as a MultiSelect).
// Keep the field keys in sync with the backend FILTERABLE_FIELDS allow-list.
const FILTER_FIELDS = [
  { key: "name",          label: "Name",                  type: "text" },
  { key: "mobile",        label: "Phone",                 type: "phone" },
  { key: "alt_mobile",    label: "Alternate Phone",       type: "phone" },
  { key: "whatsapp",      label: "WhatsApp Number",       type: "phone" },
  { key: "email",         label: "Email",                 type: "text" },
  { key: "city",          label: "City",                  type: "text" },
  { key: "state",         label: "State",                 type: "text" },
  { key: "country",       label: "Country",               type: "text" },
  { key: "gender",        label: "Gender",                type: "text" },
  { key: "language",      label: "Language",              type: "text" },
  { key: "friendly_id",   label: "Friendly ID",           type: "text" },
  { key: "remark",        label: "Remark",                type: "text" },
  { key: "wants_visit",   label: "Would You Like to Visit Us", type: "text" },
  { key: "sub_status",    label: "Sub Status",            type: "text" },
  { key: "utm_source",    label: "UTM Source",            type: "text" },
  { key: "utm_medium",    label: "UTM Medium",            type: "text" },
  { key: "utm_campaign",  label: "UTM Campaign",          type: "text" },
  { key: "source",        label: "Source",                type: "option", optKey: "sources" },
  { key: "sub_source",    label: "Sub Source",            type: "option", optKey: "subSources" },
  { key: "stage",         label: "Stage",                 type: "option", optKey: "stages" },
  { key: "status",        label: "Status",                type: "option", optKey: "statuses" },
  { key: "category",      label: "Category",              type: "option", optKey: "categories" },
  { key: "concern",       label: "Concern",               type: "option", optKey: "concerns" },
  { key: "assigned_to",   label: "Owner",                 type: "option", optKey: "owners" },
  { key: "branch_id",     label: "Branch",                type: "option", optKey: "branches" },
  { key: "deal_value",    label: "Deal Value",            type: "number" },
  { key: "revenue",       label: "Revenue",               type: "number" },
  { key: "created_at",    label: "Lead Created At",       type: "date" },
  { key: "appointment_date",   label: "Appointment Date",  type: "date" },
  { key: "appointment_booked_at", label: "Appointment Booked On", type: "date" },
  { key: "next_follow_up",     label: "Next Followup On",  type: "date" },
  { key: "first_contacted_on", label: "First Contacted On", type: "date" },
  { key: "last_contacted_on",  label: "Last Contacted On", type: "date" },
  { key: "assigned_at",        label: "Lead Assigned On",  type: "date" },
  { key: "last_interaction",   label: "Last Interaction",  type: "text" },
];

const FIELD_BY_KEY = Object.fromEntries(FILTER_FIELDS.map((f) => [f.key, f]));

// Operators available per field type. `arity` tells the value renderer how many
// inputs to show; `value: "none"` operators (is empty) take no value.
const OPERATORS = {
  text: [
    { value: "contains",     label: "Contains" },
    { value: "not_contains", label: "Not Contains" },
    { value: "equals",       label: "Equals" },
    { value: "not_equals",   label: "Not Equals" },
    { value: "starts_with",  label: "Starts With" },
    { value: "is_empty",     label: "Is Empty",     arity: 0 },
    { value: "is_not_empty", label: "Is Not Empty", arity: 0 },
  ],
  phone: [
    { value: "contains",     label: "Contains" },
    { value: "equals",       label: "Equals" },
    { value: "not_equals",   label: "Not Equals" },
    { value: "starts_with",  label: "Starts With" },
    { value: "is_empty",     label: "Is Empty",     arity: 0 },
    { value: "is_not_empty", label: "Is Not Empty", arity: 0 },
  ],
  option: [
    { value: "equals",       label: "Equals" },
    { value: "not_equals",   label: "Not Equals" },
    { value: "is_empty",     label: "Is Empty",     arity: 0 },
    { value: "is_not_empty", label: "Is Not Empty", arity: 0 },
  ],
  number: [
    { value: "equals",       label: "Equals" },
    { value: "not_equals",   label: "Not Equals" },
    { value: "gt",           label: "Greater Than" },
    { value: "lt",           label: "Less Than" },
    { value: "between",      label: "Between",      arity: 2 },
    { value: "not_between",  label: "Not Between",  arity: 2 },
    { value: "is_empty",     label: "Is Empty",     arity: 0 },
  ],
  date: [
    { value: "between",      label: "Between",      arity: 2 },
    { value: "not_between",  label: "Not Between",  arity: 2 },
    { value: "on",           label: "On" },
    { value: "before",       label: "Before" },
    { value: "after",        label: "After" },
    { value: "is_empty",     label: "Is Empty",     arity: 0 },
  ],
};

const opMeta = (type, op) => OPERATORS[type]?.find((o) => o.value === op);
const newRule = () => ({ field: "", op: "", value: "", value2: "" });

/**
 * Full-screen detailed-filters workspace. The left panel is a rule builder:
 * stack any number of {field, operator, value} rules with "Add Filter"; each
 * rule's operator set and value input adapt to the field type. Matching leads
 * render on the right. Rules serialize to a JSON `filters` query param honored
 * by /leads, /leads/export and the bulk assign/delete endpoints.
 */
export default function LeadFilters() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = ["super_admin", "hq_admin"].includes(user?.role);
  // hq_admin has full parity with super_admin (backend ADMIN_BYPASS_ROLES).
  const isSuperAdmin = ["super_admin", "hq_admin"].includes(user?.role);
  const canAssign = ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"].includes(user?.role);

  const md = useMasterData();

  const [allUsers, setAllUsers] = useState([]);
  const [telecallers, setTelecallers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  React.useEffect(() => {
    api.get("/users", { params: { role: "telecaller" } }).then((r) => setTelecallers(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    // Ungated: the Assign dialog's manual picker needs the full roster, and
    // branch_manager / franchisee_owner can assign without being "admin" here.
    api.get("/users").then((r) => setAllUsers(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/franchises").then((r) => setFranchises(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  // Who a manual assignment may target — every active user, not just tele-callers.
  // /users includes deactivated staff, and parking leads on someone who can't log in
  // makes them unworkable, so they're filtered out rather than offered.
  const assignableUsers = useMemo(
    () => allUsers.filter((u) => u.active !== false),
    [allUsers],
  );

  // Option lists keyed to match FILTER_FIELDS[].optKey → [{value,label}].
  const optionsFor = useCallback((optKey) => {
    switch (optKey) {
      case "stages":     return (md.stages || []).map((s) => ({ value: s.value, label: s.label }));
      case "statuses":   return (md.statuses || []).map((s) => ({ value: s.value, label: s.label }));
      case "subSources": return (md.subSources || []).map((s) => ({ value: s.value, label: s.label }));
      case "sources":    return (md.sources || []).map((s) => ({ value: s, label: s }));
      case "categories": return (md.categories || []).map((c) => ({ value: c, label: c }));
      case "concerns":   return (md.concerns || []).map((c) => ({ value: c, label: c }));
      case "owners":     return (isAdmin ? allUsers : telecallers).map((u) => ({ value: u.id, label: u.name }));
      case "branches":   return (branches || []).map((b) => ({ value: b.id, label: b.name }));
      default: return [];
    }
  }, [md, allUsers, telecallers, isAdmin, branches]);

  // ── Rule builder state, hydrated from an incoming `filters` param ──
  const [rules, setRules] = useState(() => {
    try {
      const raw = params.get("filters");
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed) && parsed.length) {
        return parsed.map((r) => ({
          field: r.field || "", op: r.op || "",
          value: r.value ?? "", value2: r.value2 ?? "",
        }));
      }
    } catch { /* ignore malformed incoming param */ }
    return [newRule()];
  });

  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(() => {
    const p = parseInt(params.get("page") || "1", 10);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [selected, setSelected] = useState(new Set());
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  // Column visibility — shares storage with the Leads list so choices sync.
  const [visibleCols, setVisibleCols] = useState(() => {
    try { const s = localStorage.getItem(COL_STORAGE_KEY); return s ? JSON.parse(s) : DEFAULT_VISIBLE_COLS; } catch { return DEFAULT_VISIBLE_COLS; }
  });
  useEffect(() => { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(visibleCols)); }, [visibleCols]);
  const [colChooserOpen, setColChooserOpen] = useState(false);

  // Preserve tab + free-text search from wherever the user came from.
  const tab = params.get("tab") || "";
  const search = params.get("search") || "";

  const updateRule = (i, patch) => {
    setRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeRule = (i) => {
    setRules((rs) => (rs.length <= 1 ? [newRule()] : rs.filter((_, idx) => idx !== i)));
  };
  const addRule = () => setRules((rs) => [...rs, newRule()]);

  // A rule is "complete" (worth sending) when it has field+op and — unless the
  // operator takes no value — a value (both bounds for 2-arity operators).
  // Option values are arrays (multi-select), so "has a value" means non-empty.
  const hasValue = (v) => (Array.isArray(v) ? v.length > 0 : v !== "");
  const isComplete = (r) => {
    if (!r.field || !r.op) return false;
    const f = FIELD_BY_KEY[r.field];
    const meta = opMeta(f?.type, r.op);
    if (!meta) return false;
    if (meta.arity === 0) return true;
    if (meta.arity === 2) return hasValue(r.value) && hasValue(r.value2);
    return hasValue(r.value);
  };

  const validRules = useMemo(
    () => rules.filter(isComplete).map((r) => {
      const meta = opMeta(FIELD_BY_KEY[r.field]?.type, r.op);
      const out = { field: r.field, op: r.op };
      if (meta.arity !== 0) out.value = r.value;
      if (meta.arity === 2) out.value2 = r.value2;
      return out;
    }),
    [rules],
  );

  // Shared param builder for search / export / assign / open-in-list.
  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (tab) p.set("tab", tab);
    if (search) p.set("search", search);
    if (validRules.length) p.set("filters", JSON.stringify(validRules));
    return p;
  }, [tab, search, validRules]);

  const runSearch = useCallback(async (goToPage = 1) => {
    // Guard: when called directly from onClick, React passes the event object.
    // Coerce anything non-numeric back to page 1.
    const pageNum = Number.isInteger(goToPage) && goToPage > 0 ? goToPage : 1;
    setLoading(true);
    setSearched(true);
    try {
      const p = buildParams();
      p.set("page", String(pageNum));
      p.set("limit", String(RESULT_LIMIT));
      const res = await api.get(`/leads?${p.toString()}`);
      setResults(res.data);
      setPage(pageNum);
      // Deliberately does NOT touch the selection — runSearch is also how paging
      // happens, and dropping the ticks on Next made a cross-page selection
      // impossible. Callers that DO mean "start over" (the Search button, and the
      // post-bulk-action refreshes) call clearSelection() themselves.
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Mirror the rule builder + page into the URL (like the Leads list does) so
  // opening a lead detail and pressing Back restores the full filter workspace
  // instead of clearing it. Only non-empty values are written.
  useEffect(() => {
    const next = {};
    if (tab) next.tab = tab;
    if (search) next.search = search;
    if (validRules.length) next.filters = JSON.stringify(validRules);
    if (page > 1) next.page = String(page);
    setParams(next, { replace: true });
  }, [validRules, tab, search, page, setParams]);

  // On first mount, if we arrived carrying filters (e.g. via Back from a lead
  // detail), immediately re-run the search so the results table repopulates.
  const didInitialSearch = useRef(false);
  useEffect(() => {
    if (didInitialSearch.current) return;
    didInitialSearch.current = true;
    if (validRules.length) runSearch(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil((results.total || 0) / RESULT_LIMIT));

  const toggleSelect = (id) => {
    setSelectAllFiltered(false);
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAllOnPage = () => {
    setSelectAllFiltered(false);
    setSelected((prev) => {
      const pageIds = results.items.map((l) => l.id);
      const allSelected = pageIds.every((id) => prev.has(id));
      const next = new Set(prev);
      pageIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };
  // Whole current page is checked (drives the "select all N results" banner).
  const allOnPage = results.items.length > 0 && results.items.every((l) => selected.has(l.id));
  const hasSelection = selected.size > 0 || selectAllFiltered;
  const clearSelection = () => { setSelected(new Set()); setSelectAllFiltered(false); };

  const reset = () => setRules([newRule()]);

  // Plain-object form for the bulk assign/delete endpoints ({select_all, filters}).
  const filtersObject = () => Object.fromEntries(buildParams().entries());

  const handleBulkDelete = async () => {
    // Mirror the actions-menu selection contract: explicit picks delete just
    // those; otherwise (select-all banner, or nothing checked) delete all matches.
    const explicit = selected.size > 0 && !selectAllFiltered;
    const count = explicit ? selected.size : results.total;
    if (count === 0) return;
    if (!window.confirm(`Permanently delete ${count} lead(s)? This cannot be undone.`)) return;
    try {
      const payload = explicit
        ? { lead_ids: [...selected] }
        : { select_all: true, filters: filtersObject() };
      const { data: res } = await api.post("/leads/bulk-delete", payload);
      toast.success(`Deleted ${res.deleted} lead(s)`);
      clearSelection();
      runSearch(page);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openOnLeadsList = () => {
    navigate({ pathname: "/leads", search: `?${buildParams().toString()}` });
  };

  const handleExport = async (fields, { maskContacts } = {}) => {
    setExporting(true);
    try {
      const token = localStorage.getItem("dermatrix_token");
      const p = buildParams();
      if (fields && fields.length < EXPORT_ALL_FIELDS.length) p.set("fields", fields.join(","));
      if (maskContacts) p.set("mask_contacts", "1");
      const res = await fetch(`${API_BASE}/leads/export?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    // Owns the full viewport — no app sidebar/topbar (rendered via fullPage route).
    <div className="flex h-screen flex-col overflow-hidden bg-cream md:flex-row">
      {/* ─── Left side panel: rule builder ─── */}
      <aside className="flex w-full shrink-0 flex-col border-b border-navy/10 bg-white md:w-96 md:border-b-0 md:border-r">
        <div className="flex items-center gap-2 border-b border-navy/10 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-8 w-8 text-navy">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold text-navy">Filters</h1>
        </div>

        {/* Scrollable rule body */}
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {/* Select View — saved views not implemented yet; defaults to All. */}
          <FilterField label="Select View">
            <Select value="all" disabled>
              <SelectTrigger data-testid="filter-view"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem></SelectContent>
            </Select>
          </FilterField>

          <div className="space-y-3 border-t border-navy/10 pt-3">
            {rules.map((rule, i) => (
              <RuleRow
                key={i}
                index={i}
                rule={rule}
                optionsFor={optionsFor}
                onChange={(patch) => updateRule(i, patch)}
                onRemove={() => removeRule(i)}
                removable={rules.length > 1 || rule.field !== ""}
              />
            ))}

            <Button variant="outline" onClick={addRule} data-testid="filters-add-btn"
              className="w-full border-dashed border-navy/25 text-navy hover:bg-navy/5">
              <Plus className="mr-1.5 h-4 w-4" /> Add Filter
            </Button>
          </div>
        </div>

        {/* Search / Reset pinned to the bottom */}
        <div className="flex items-center gap-2 border-t border-navy/10 px-4 py-3">
          <Button variant="ghost" onClick={reset} className="text-navy" data-testid="filters-reset-btn">
            <RotateCcw className="mr-1.5 h-4 w-4" /> Reset
          </Button>
          {/* A new search means new criteria, so the old picks are meaningless — and
              leaving selectAllFiltered set would silently re-point a bulk action at
              the NEW result set. Clear before running. */}
          <Button onClick={() => { clearSelection(); runSearch(1); }} disabled={loading} className="flex-1 bg-navy text-white hover:bg-navy-hover" data-testid="filters-search-btn">
            <Search className="mr-1.5 h-4 w-4" /> {loading ? "Searching…" : "Search"}
          </Button>
        </div>
      </aside>

      {/* ─── Right side: results ─── */}
      <main className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-navy/10 px-5 py-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-navy">Search Results</h2>
            <p className="text-xs text-muted-foreground">
              {searched ? `${results.total} lead${results.total === 1 ? "" : "s"} match` : "Add filters, then Search to preview leads"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setColChooserOpen(true)} data-testid="filters-columns-btn">
              <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Columns
            </Button>
            <LeadActionsMenu
              context="filters"
              selected={[...selected]}
              selectAllFiltered={selectAllFiltered}
              total={results.total}
              hasSelection={hasSelection}
              filters={filtersObject()}
              canAct={searched && results.total > 0}
              isSuperAdmin={isSuperAdmin}
              canAssign={canAssign}
              onAssign={() => setAssignOpen(true)}
              onDelete={handleBulkDelete}
              onExport={handleExport}
              exporting={exporting}
              onOpenInList={openOnLeadsList}
              onRefresh={() => { clearSelection(); runSearch(page); }}
            />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col p-4">
          {!searched ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Add your filters on the left and click <span className="mx-1 font-medium text-navy">Search</span> to see matching leads.
            </div>
          ) : loading ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading…</div>
          ) : results.items.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No leads match these filters.</div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
              {/* Select-all banner — offer to extend a full-page selection to all matches. */}
              {allOnPage && results.total > results.items.length && (
                <div className="flex items-center justify-center gap-2 border-b border-gold/30 bg-gold/5 px-4 py-2 text-sm">
                  {selectAllFiltered ? (
                    <>
                      <span className="font-medium text-navy">All {results.total} leads matching these filters are selected.</span>
                      <button onClick={clearSelection} className="font-semibold text-gold hover:underline">Clear selection</button>
                    </>
                  ) : (
                    <>
                      <span className="text-navy">All {results.items.length} leads on this page are selected.</span>
                      <button onClick={() => setSelectAllFiltered(true)} className="font-semibold text-gold hover:underline">Select all {results.total} leads</button>
                    </>
                  )}
                </div>
              )}
              <div className="flex-1 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-20">
                    <tr className="border-b bg-cream text-left text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                      <th className="px-3 py-2.5 w-10">
                        <Checkbox
                          checked={allOnPage}
                          onCheckedChange={toggleSelectAllOnPage}
                          aria-label="Select all on page"
                        />
                      </th>
                      {visibleCols.map((key) => (
                        <th key={key} className="px-3 py-2.5">{COL_MAP[key]?.label || key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.items.map((l) => {
                      // Row tint mirrors the Leads list: a manual "Set Lead Color"
                      // wins; otherwise blue = the lead has been contacted before.
                      // (No tabs/worklists here, so the default touched rule applies.)
                      const colorBg = l.color ? LEAD_COLOR_BG[l.color] : null;
                      // Only count contact from the current cycle — a repeat
                      // enquiry refreshes created_at, so an old last_contacted_on
                      // shouldn't tint a fresh repeat as touched.
                      const touched = contactedThisCycle(l);
                      const rowTint = colorBg || (touched ? "bg-blue-100/80 hover:bg-blue-200/80" : "hover:bg-nude/20");
                      return (
                      <tr key={l.id}
                        onClick={() => navigate(`/leads/${l.id}`)}
                        className={`border-b transition-colors last:border-0 cursor-pointer ${selected.has(l.id) ? "bg-nude/30" : rowTint}`}
                        data-testid={`filter-row-${l.id}`}>
                        <td className="px-3 py-2 w-10" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected.has(l.id)}
                            onCheckedChange={() => toggleSelect(l.id)}
                            aria-label={`Select ${l.name || maskPhone(l.mobile)}`}
                          />
                        </td>
                        {visibleCols.map((key) => renderLeadCell(COL_MAP[key] || {}, key, l))}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between border-t border-navy/10 px-4 py-2.5 text-xs text-muted-foreground">
                <span>
                  {selectAllFiltered ? `All ${results.total} selected · ` : selected.size > 0 ? `${selected.size} selected · ` : ""}
                  Showing {(page - 1) * RESULT_LIMIT + 1}–{Math.min(page * RESULT_LIMIT, results.total)} of {results.total}
                </span>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-7 px-2"
                    disabled={page <= 1 || loading} onClick={() => runSearch(page - 1)}>
                    <ChevronLeft className="h-3.5 w-3.5" /> Prev
                  </Button>
                  <span className="px-2">Page {page} / {totalPages}</span>
                  <Button variant="outline" size="sm" className="h-7 px-2"
                    disabled={page >= totalPages || loading} onClick={() => runSearch(page + 1)}>
                    Next <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Export dialog is owned by LeadActionsMenu now. */}
      <AssignDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        selected={[...selected]}
        selectAllFiltered={selectAllFiltered}
        filters={filtersObject()}
        total={selectAllFiltered ? results.total : selected.size}
        users={assignableUsers}
        branches={branches}
        franchises={franchises}
        isAdmin={isAdmin}
        onDone={() => { setAssignOpen(false); clearSelection(); runSearch(page); }}
      />
      <ColumnChooserDialog open={colChooserOpen} onOpenChange={setColChooserOpen} columns={ALL_COLUMNS} visibleCols={visibleCols} onApply={setVisibleCols} />
    </div>
  );
}

// A single field / operator / value rule.
function RuleRow({ index, rule, optionsFor, onChange, onRemove, removable }) {
  const field = FIELD_BY_KEY[rule.field];
  const ops = field ? OPERATORS[field.type] : [];
  const meta = field ? opMeta(field.type, rule.op) : null;

  return (
    <div className="rounded-lg border border-navy/10 bg-cream/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-navy/50">
          {index === 0 ? "Where" : "And"}
        </span>
        {removable && (
          <button onClick={onRemove} data-testid={`filters-remove-${index}`}
            className="rounded p-0.5 text-navy/40 hover:bg-navy/10 hover:text-navy">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Select Filter (field) — searchable + scrollable; reset op/value on change. */}
      <FilterField label="Select Filter">
        <SearchableSelect
          options={FILTER_FIELDS.map((f) => ({ value: f.key, label: f.label }))}
          value={rule.field || ""}
          onChange={(v) => onChange({ field: v, op: "", value: "", value2: "" })}
          placeholder="Select field"
          searchPlaceholder="Search fields…"
          className="h-9"
        />
      </FilterField>

      {/* Select Operator */}
      {field && (
        <div className="mt-2">
          <FilterField label="Select Operator">
            <Select
              value={rule.op || undefined}
              onValueChange={(v) => onChange({ op: v, value: "", value2: "" })}
            >
              <SelectTrigger data-testid={`filter-op-${index}`}><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {ops.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
        </div>
      )}

      {/* Filter Value — shape depends on field type + operator arity */}
      {field && meta && meta.arity !== 0 && (
        <div className="mt-2">
          <FilterField label="Filter Value">
            <ValueInput field={field} rule={rule} optionsFor={optionsFor} onChange={onChange} index={index} isRange={meta.arity === 2} />
          </FilterField>
        </div>
      )}
    </div>
  );
}

function ValueInput({ field, rule, optionsFor, onChange, index, isRange }) {
  const inputCls = "h-9 w-full rounded-md border border-navy/15 bg-white px-2 text-sm";

  if (field.type === "option") {
    // Equality against an option list — multi-select. `value` is stored as an
    // array of the selected option values; the backend maps equals→$in and
    // not_equals→$nin over the array (single value is treated as a 1-item list).
    const selected = Array.isArray(rule.value)
      ? rule.value
      : rule.value
      ? [rule.value]
      : [];
    return (
      <MultiSelect
        className="w-full"
        placeholder="Select value(s)"
        options={optionsFor(field.optKey)}
        selected={selected}
        onChange={(vals) => onChange({ value: vals })}
      />
    );
  }

  if (field.type === "number") {
    if (isRange) {
      return (
        <div className="flex items-center gap-1.5">
          <input type="number" value={rule.value} placeholder="From" className={inputCls}
            data-testid={`filter-value-${index}`} onChange={(e) => onChange({ value: e.target.value })} />
          <span className="text-navy/40">→</span>
          <input type="number" value={rule.value2} placeholder="To" className={inputCls}
            data-testid={`filter-value2-${index}`} onChange={(e) => onChange({ value2: e.target.value })} />
        </div>
      );
    }
    return (
      <input type="number" value={rule.value} placeholder="Value" className={inputCls}
        data-testid={`filter-value-${index}`} onChange={(e) => onChange({ value: e.target.value })} />
    );
  }

  if (field.type === "date") {
    if (isRange) {
      return (
        <DateRangePicker
          from={rule.value}
          to={rule.value2}
          onChange={({ from, to }) => onChange({ value: from, value2: to })}
          index={index}
        />
      );
    }
    return (
      <DatePicker
        value={rule.value}
        onChange={(v) => onChange({ value: v })}
        index={index}
      />
    );
  }

  // text / phone. equals/not_equals accept a comma-separated list (matches any).
  const multiHint = rule.op === "equals" || rule.op === "not_equals";
  return (
    <input type="text" value={rule.value}
      placeholder={multiHint ? "Enter value(s), comma-separated" : "Enter value"}
      className={inputCls}
      data-testid={`filter-value-${index}`} onChange={(e) => onChange({ value: e.target.value })} />
  );
}

// ── Date pickers ─────────────────────────────────────────────────────────────
// Values are exchanged as "YYYY-MM-DD" strings (what the backend rule builder
// expects); the calendars work in Date objects internally.
const CURRENT_YEAR = new Date().getFullYear();
const toDate = (s) => {
  if (!s) return undefined;
  try { return parseISO(s); } catch { return undefined; }
};
const toStr = (d) => (d ? format(d, "yyyy-MM-dd") : "");
const triggerCls =
  "flex h-9 w-full items-center justify-between rounded-md border border-navy/15 bg-white px-2 text-left text-sm";

// Dual-month range picker rendered as a single field: "from → to".
function DateRangePicker({ from, to, onChange, index }) {
  const [open, setOpen] = React.useState(false);
  const selected = { from: toDate(from), to: toDate(to) };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={triggerCls} data-testid={`filter-value-${index}`}>
          <span className={from ? "text-ink" : "text-muted-foreground"}>
            {from || "From"} <span className="mx-1 text-navy/40">→</span> {to || "To"}
          </span>
          <CalendarIcon className="h-4 w-4 shrink-0 text-navy/40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          numberOfMonths={2}
          captionLayout="dropdown-buttons"
          fromYear={CURRENT_YEAR - 6}
          toYear={CURRENT_YEAR + 3}
          classNames={{
            caption_dropdowns: "flex items-center gap-1",
            dropdown: "rounded-md border border-navy/15 bg-white px-1 py-0.5 text-sm text-navy",
            vhidden: "sr-only",
          }}
          defaultMonth={selected.from || new Date()}
          selected={selected}
          onSelect={(range) => onChange({ from: toStr(range?.from), to: toStr(range?.to) })}
        />
      </PopoverContent>
    </Popover>
  );
}

// Single-date picker for On / Before / After.
function DatePicker({ value, onChange, index }) {
  const [open, setOpen] = React.useState(false);
  const selected = toDate(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={triggerCls} data-testid={`filter-value-${index}`}>
          <span className={value ? "text-ink" : "text-muted-foreground"}>{value || "Select date"}</span>
          <CalendarIcon className="h-4 w-4 shrink-0 text-navy/40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown-buttons"
          fromYear={CURRENT_YEAR - 6}
          toYear={CURRENT_YEAR + 3}
          classNames={{
            caption_dropdowns: "flex items-center gap-1",
            dropdown: "rounded-md border border-navy/15 bg-white px-1 py-0.5 text-sm text-navy",
            vhidden: "sr-only",
          }}
          defaultMonth={selected || new Date()}
          selected={selected}
          onSelect={(d) => { onChange(toStr(d)); setOpen(false); }}
        />
      </PopoverContent>
    </Popover>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-navy/60">{label}</label>
      {children}
    </div>
  );
}
